import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import parsePhoneNumber from 'libphonenumber-js'
import { When } from 'react-if'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { BlockedIcon } from '@/atoms/Icons/BlockedIcon'
import { StarFilledIcon } from '@/atoms/Icons/StarFilledIcon'
import { StarOutlineIcon } from '@/atoms/Icons/StarOutlineIcon'
import { AsH3, CaptionMD, CaptionSM, Text, TitleXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { UNITED_STATES } from '@/constants/regions'
import { useThemeContext } from '@/contexts/ThemeContext'
import { useExperimentContext } from '@/experimentation/hooks'
import { useToast } from '@/molecules/Toast'
import { useTheaterPreferences } from '@/organisms/TheaterPreferences'
import { TheatricalReleaseResult } from '@/services/Theatrical'
import { getSortedShowtimes } from '@/services/TicketsService'
import { isUnitedStatesOrCanada } from '@/services/UserRegionService'
import { useUser } from '@/services/UserService'
import { TheatricalShowtime } from '@/types/codegen-federation'
import { mergeUrlParams } from '@/utils/UriBuilder/UriBuilder'
import { useSafeTrack } from '@/utils/analytics'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useLoginUrl } from '@/utils/auth-utils'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { getStringFromSessionStorage, removeFromSessionStorage, writeToSessionStorage } from '@/utils/session-storage'
import { useTranslate } from '@/utils/translate/translate-client'
import ShowtimesList from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimesList'
import ShowtimesWithNoUrlsState from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimesWithNoUrlsState'
import { useTicketsPromoContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsPromo/TicketsPromoContext'
import { BaseTooltip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/ToolTip'
import { LocaleWarningModalState } from '@/views/TicketCheckoutViews/ShowtimesView/components/VenueList'
import VenuePills from '@/views/TicketCheckoutViews/ShowtimesView/components/VenuePills'
import { GroupTicketsModalTypes } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GroupTicketsModal/GroupTicketsModal'
import {
  isPartnerVenue,
  isShowtimePast,
  sanitizePhoneNumber,
} from '@/views/TicketCheckoutViews/ShowtimesView/utils/utils'
import { Flow, generateTrackingPayload } from '@/views/TicketCheckoutViews/utils'
import { useTheatricalPromotionContext } from '../../components/TheatricalPromotionContext'
import { VenueLogo } from '../../components/VenueLogo'
import { ShowtimesVenue } from '../hooks/useVenueSearch/useVenueSearch'
import { isPremierePartnerVenueByVendor } from '../utils/premierePartnerUtils'
import { GroupTicketsButtonWrapper } from './GroupTicketsButton'
import { SeatAvailabilityDrawer } from './SeatAvailability/SeatAvailabilityDrawer'
import { SingAlongModal } from './SingAlongModal'
import { useTicketsContext } from './TicketsContext'

const autoScrollToVenue = ({ venueId, venueRef }: { venueId: string; venueRef: React.RefObject<HTMLLIElement> }) => {
  const sessionVenueId = getStringFromSessionStorage('venueId')
  if (sessionVenueId === venueId) {
    venueRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
    removeFromSessionStorage('venueId')
  }
}

export interface VenueItemProps {
  venue: ShowtimesVenue
  theatricalRelease: TheatricalReleaseResult
  theaterSlug: string
  isGroupTicketsModalOpen?: boolean
  openGroupTicketsModal?: (type: GroupTicketsModalTypes) => void
  handleOpenAutoBookModal?: () => void
  handleOpenPreferencesModal?: () => void
  setLocaleWarningModalState: React.Dispatch<React.SetStateAction<LocaleWarningModalState>>
  reservationId?: string
  showtimesDisabled: boolean
  setDisableShowtimes: React.Dispatch<React.SetStateAction<boolean>>
  zIndex: number
  shouldShowNewBadge?: boolean
}

export const VenueItem: FC<VenueItemProps> = ({
  venue,
  theaterSlug,
  theatricalRelease,
  reservationId,
  openGroupTicketsModal,
  setLocaleWarningModalState,
  showtimesDisabled,
  setDisableShowtimes,
  shouldShowNewBadge = false,
}) => {
  const { push, asPath, query } = useSafeRouter()
  const track = useSafeTrack()
  const { trackProductAdded } = useEcommerceEvents()
  const { userLocation, isLoggedIn } = useUser()
  const venueRef = useRef<HTMLLIElement>(null)
  const { t } = useTranslate('tickets')
  const { showToast } = useToast()
  const { isDarkMode } = useThemeContext()
  const { promo } = useTheatricalPromotionContext()
  const { getFeatureValue } = useExperimentContext()
  const [showSingAlongModal, setShowSingAlongModal] = useState(false)
  const [selectedShowtime, setSelectedShowtime] = useState<TheatricalShowtime | null>(null)

  const { latitude, longitude } = userLocation?.coordinates ?? {}
  const {
    guildTicketsCount,
    handleLanguageWarning,
    trackTicketIntent,
    detectLanguageDifference,
    basicPageExperimentEnabled,
    experiments,
  } = useTicketsContext()
  const { selectedVenueId, handleVenueSelect, handleSaveVenuePreferenceOnly } = useTheaterPreferences()
  const flow = getItemOrFirstEntry(query.flow)

  // Seat Availability Feature
  const isSeatAvailabilityEnabled = !!experiments?.['showtime_seat_availability_v3']
  const [isSeatAvailabilityOpen, setIsSeatAvailabilityOpen] = useState(false)

  const handleToggleSeatAvailability = useCallback(() => {
    setIsSeatAvailabilityOpen((prev) => !prev)
  }, [])

  const redirectBackPath = useMemo(() => {
    return mergeUrlParams(asPath, { preferred_venue_id: venue.providerId })
  }, [asPath, venue.providerId])

  const { loginUrl, trackLoginStarted } = useLoginUrl(redirectBackPath)

  const promoContext = useTicketsPromoContext()
  const isAngelPartnerVenue = isPartnerVenue(venue.ticketingProviderSlug)

  // Filter out showtimes that don't have a UTC start time, or are reservable but don't have total seats
  const formattedShowtimes = useMemo(
    () =>
      getSortedShowtimes(
        venue.showtimes.filter((showTime) => {
          return showTime.utcStartTime && (!showTime.reservable || (showTime.totalSeats && showTime.totalSeats > 0))
        }),
      ),
    [venue.showtimes],
  )

  const hasAnySeatmapPreviews = useMemo(() => {
    return formattedShowtimes.some((showtime) => !!showtime.seatmap)
  }, [formattedShowtimes])

  const venuePhoneNumber = useMemo(
    () => parsePhoneNumber(sanitizePhoneNumber(venue.phone), { defaultCountry: UNITED_STATES }),
    [venue.phone],
  )

  const doShowtimesHavePurchaseUrl = venue.showtimes.every((s) => !!s?.ticketUrl)
  const countryCode = theatricalRelease?.region?.countryCode ?? UNITED_STATES
  const isPhilippinesFreeTicketFlow = promo && countryCode === 'PH'
  const hasNoShowtimes = formattedShowtimes.length === 0
  const isVenueIntegrated = !!reservationId && !!theaterSlug && isAngelPartnerVenue
  const logoUrl = venue.vendorLogo?.cloudinaryPath
  const isMarcusVenue = venue.name.includes('Marcus')
  const shouldShowGroupTicketsButton =
    isAngelPartnerVenue && !!theatricalRelease?.region?.isGroupTicketsEnabled && flow !== Flow.GROUP_TICKETS

  const analyticsPayload = useMemo(() => {
    return {
      venue: { ...venue, showtimes: venue.showtimes?.map((showtime) => ({ id: showtime?.id })) ?? [] },
      latitude,
      longitude,
      theatricalName: theatricalRelease?.title || '',
      theatricalSlug: theatricalRelease?.theatricalSlug,
      projectSlug: theatricalRelease?.projectSlug,
    }
  }, [venue, theatricalRelease, latitude, longitude])

  const handleSendShowTimeClickEvents = useCallback(
    async (showTime: TheatricalShowtime) => {
      const trackingPayload = generateTrackingPayload(showTime, analyticsPayload, reservationId)

      // Fire all analytics calls in parallel to reduce total time and prevent blocking
      const analyticsPromises = [
        track('Tickets Showtime Selected', {
          ...trackingPayload,
        }),
        track(`Tickets Showtime Selected ${theatricalRelease?.theatricalSlug}`, {
          ...trackingPayload,
        }),
        trackProductAdded({
          category: 'theatrical ticket',
          funnel: 'theatrical',
          sku: `ticket-${theatricalRelease?.theatricalSlug}`,
          product_id: `ticket-${theatricalRelease?.theatricalSlug}`,
          name: `${theatricalRelease?.theatricalSlug} theatrical ticket`,
          // ? if we could get the price for the showtime selected other wise this is
          // ! the value we are telling marketing the action is worth
          price: 15,
          currency: 'USD',
          quantity: 1,
          projectSlug: theatricalRelease?.theatricalSlug,
        }),
        isAngelPartnerVenue
          ? track('Integraged Tickets Showtime Selected', {
              ...trackingPayload,
            })
          : track('Non Integraged Tickets Showtime Selected', {
              ...trackingPayload,
            }),
      ]

      const isPremiereVenueClick = isPremierePartnerVenueByVendor({
        vendorId: venue.vendorId,
        theaterSlug,
      })

      if (isPremiereVenueClick) {
        analyticsPromises.push(
          track('Premiere Badged Venue Clicked', {
            ...trackingPayload,
          }),
        )
      }

      // Fire all analytics in parallel - track the promise for error handling
      // We don't await completion, but we do track it so the caller can wait for initiation
      const analyticsPromise = Promise.allSettled(analyticsPromises)

      // Track ticket intent (non-blocking)
      trackTicketIntent(analyticsPayload)

      // Return the promise so caller can wait for analytics to initiate
      return analyticsPromise
    },
    [
      analyticsPayload,
      isAngelPartnerVenue,
      reservationId,
      theaterSlug,
      theatricalRelease?.theatricalSlug,
      track,
      trackProductAdded,
      trackTicketIntent,
      venue.vendorId,
    ],
  )

  const handleAngelCheckoutNavigation = useCallback(
    async (showTime: TheatricalShowtime) => {
      // Fallback to opening the ticket URL in a new tab if the ticketing ID is not available
      if (!showTime.ticketingId && showTime.ticketUrl) {
        setDisableShowtimes(false)
        return window.open(showTime.ticketUrl, '_blank')
      } else if (!showTime.ticketingId && !showTime.ticketUrl) {
        throw new Error('No ticketing ID or ticket URL found for this showtime')
      }

      if (showTime.reservable) {
        const reservableUrl = paths.tickets.checkout.seatMap(theaterSlug, showTime.ticketingId, reservationId)
        const reservableUrlWithSelection = paths.tickets.checkout.selection(
          theaterSlug,
          showTime.ticketingId,
          reservationId,
        )

        const seatRatesExperiment = getFeatureValue('seat-rates-v1.5', false)

        const targetUrl = seatRatesExperiment ? reservableUrlWithSelection : reservableUrl
        const baseParams: Record<string, string> = {}
        if (basicPageExperimentEnabled) {
          baseParams.basicPageExperimentEnabled = 'true'
        }
        if (flow) return push(mergeUrlParams(targetUrl, { ...baseParams, flow }))
        if (promo) return push(mergeUrlParams(targetUrl, { ...baseParams, flow: promo.promoType }))
        if (basicPageExperimentEnabled) return push(mergeUrlParams(targetUrl, baseParams))
        return push(targetUrl)
      }

      const nonReservableUrl = paths.tickets.checkout.seatQuantitySelection(
        theaterSlug,
        showTime.ticketingId,
        reservationId,
      )
      if (flow) return push(mergeUrlParams(nonReservableUrl, { flow }))
      if (promo) return push(mergeUrlParams(nonReservableUrl, { flow: promo.promoType }))
      return push(nonReservableUrl)
    },
    [theaterSlug, reservationId, flow, push, promo, setDisableShowtimes, getFeatureValue, basicPageExperimentEnabled],
  )

  const handleShowtimeClick = useCallback(
    async (showTime: TheatricalShowtime, hasWarnedLanguage?: boolean, hasWarnedSingAlong?: boolean) => {
      try {
        if (showtimesDisabled) return
        if (isVenueIntegrated) setDisableShowtimes(true)
        writeToSessionStorage('venueId', venue.id)

        const isInOtherLanguage = detectLanguageDifference(showTime)
        const skipLanguageWarning = !isUnitedStatesOrCanada(countryCode) || !!hasWarnedLanguage

        if (isInOtherLanguage && !skipLanguageWarning) {
          return handleLanguageWarning(setLocaleWarningModalState, showTime, async () => {
            await handleShowtimeClick(showTime, true)
          })
        }

        if (showTime.releaseType === 'SING_ALONG' && !hasWarnedSingAlong) {
          setShowSingAlongModal(true)
          setSelectedShowtime(showTime)
          setDisableShowtimes(false)
          return
        }

        // Fire analytics and give them a short window to initiate before navigation
        // This prevents navigation from cancelling in-flight analytics requests
        // Note: handleSendShowTimeClickEvents already handles errors internally
        const analyticsPromise = handleSendShowTimeClickEvents(showTime)

        // Wait up to 300ms for analytics to initiate, but don't block longer
        // This gives requests time to start before navigation cancels them
        await Promise.race([analyticsPromise, new Promise((resolve) => setTimeout(resolve, 300))])

        if (isVenueIntegrated && promo?.promoType !== 'claim-free-ticket')
          return await handleAngelCheckoutNavigation(showTime)
        promoContext?.handleOpenPromo({ promoContext, showTime, venue, trackingPayload: analyticsPayload })
      } catch (err) {
        logger().error('Error in handleShowtimeClick', err)
        setDisableShowtimes(false)
        showToast(
          t(
            'showtimeError',
            'We encountered an error opening this showtime. Please try again later, or select another showtime.',
          ),
        )
      }
    },
    [
      showtimesDisabled,
      isVenueIntegrated,
      setDisableShowtimes,
      venue,
      detectLanguageDifference,
      countryCode,
      handleSendShowTimeClickEvents,
      handleAngelCheckoutNavigation,
      promoContext,
      analyticsPayload,
      handleLanguageWarning,
      setLocaleWarningModalState,
      promo,
      showToast,
      t,
    ],
  )

  useEffect(() => {
    autoScrollToVenue({ venueId: venue.id, venueRef: venueRef })
  }, [venue.id])

  const handleSetPreferredTheater = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation()
      if (isLoggedIn) {
        handleVenueSelect(venue.providerId, venue)
        handleSaveVenuePreferenceOnly(venue.providerId)
      } else {
        trackLoginStarted('preferred-venue-selection-showtimes-view')
        push(loginUrl)
      }
    },
    [handleSaveVenuePreferenceOnly, handleVenueSelect, venue, isLoggedIn, push, loginUrl, trackLoginStarted],
  )

  useEffect(() => {
    const preferredVenueId = getItemOrFirstEntry(query.preferred_venue_id)
    if (typeof preferredVenueId === 'string' && isLoggedIn) {
      if (venue.providerId === preferredVenueId && preferredVenueId !== selectedVenueId) {
        handleVenueSelect(preferredVenueId, venue)
        handleSaveVenuePreferenceOnly(preferredVenueId)
      }
    }
  }, [venue, query.preferred_venue_id, selectedVenueId, handleVenueSelect, handleSaveVenuePreferenceOnly, isLoggedIn])

  const handleGroupTicketClick = useCallback(() => {
    track('Clicked Group Tickets Available')
    openGroupTicketsModal && openGroupTicketsModal('tickets')
  }, [openGroupTicketsModal, track])

  const showPreferredVenueSelection = useMemo(() => {
    if (basicPageExperimentEnabled) {
      return !!isLoggedIn && !!experiments?.['preferred-theater-vanilla-layout']
    }
    return !!isLoggedIn
  }, [isLoggedIn, experiments, basicPageExperimentEnabled])

  return (
    <li
      ref={venueRef}
      className={classNames(
        'relative flex flex-col  border p-6 shadow-sm rounded-3xl',
        isDarkMode ? 'bg-core-gray-900' : 'bg-white border-core-gray-200',
      )}
    >
      <div className="justify-between gap-3 md:flex lg:flex-col xl:flex-row xl:items-start">
        <FlexColumn className="w-full gap-1">
          <div className="flex !items-start">
            {logoUrl && <VenueLogo className="mr-3 shrink-0" cloudinaryPath={logoUrl} />}
            <FlexRow className="w-full !items-start justify-between gap-2">
              <FlexColumn>
                <Text
                  as={AsH3}
                  className="photon-title-sm md:photon-title-md flex flex-wrap items-center gap-1.5"
                  color={isDarkMode ? 'white' : 'black'}
                  weight="bold"
                >
                  <span className={showPreferredVenueSelection ? 'line-clamp-1' : ''}>{venue.name}</span>
                  {!isPhilippinesFreeTicketFlow && hasNoShowtimes && (
                    <span
                      className={classNames('font-light photon-title-xs', isDarkMode ? 'text-white' : 'text-black')}
                    >
                      {t('noShowtimesParenthesis', '(No Showtimes)')}
                    </span>
                  )}
                </Text>
                <CaptionMD color={isDarkMode ? 'core-gray-400' : 'core-gray-700'}>
                  <span className={'!text-core-gray-600'}>{venue.addressText}</span>
                  {venuePhoneNumber && formattedShowtimes?.length === 0 && ` - ${venuePhoneNumber?.formatNational()}`}
                </CaptionMD>
              </FlexColumn>
              <When condition={showPreferredVenueSelection}>
                <BaseTooltip
                  buttonClassName="!rounded-full !p-2"
                  panelClassName={`!z-50 !rounded-xl`}
                  panelPosition="LEFT"
                  trigger={
                    <FlexRow className="items-center gap-2">
                      {isAngelPartnerVenue && (
                        <div className="relative cursor-pointer" onClick={handleSetPreferredTheater}>
                          <div className="relative">
                            {selectedVenueId === venue.providerId ? (
                              <StarFilledIcon size={24} color={'warning-500'} />
                            ) : (
                              <StarOutlineIcon size={24} color={'core-gray-400'} />
                            )}
                          </div>
                        </div>
                      )}
                    </FlexRow>
                  }
                  content={
                    <FlexColumn className="gap-2">
                      <FlexRow className="items-center gap-2">
                        <StarOutlineIcon size={24} color={'warning-500'} />
                        <TitleXS> {t('setPreferredTheater', 'Set Preferred Theater')}</TitleXS>
                      </FlexRow>
                      <CaptionSM color="core-gray-600">
                        {t(
                          'useStarIconToSetPreferredLocation',
                          'Use the star icon to set your preferred location for the best showtimes.',
                        )}
                      </CaptionSM>
                    </FlexColumn>
                  }
                />
              </When>
            </FlexRow>
          </div>
        </FlexColumn>
      </div>
      {!hasNoShowtimes && (
        <VenuePills
          theaterSlug={theaterSlug}
          venue={venue}
          isAngelPartnerVenue={isAngelPartnerVenue}
          guildTicketsCount={guildTicketsCount}
          className="mt-6"
          noOnlineTicketing={!doShowtimesHavePurchaseUrl}
        />
      )}
      <ShowtimesList
        className="my-6 gap-6"
        countryCode={countryCode}
        venue={venue}
        theaterSlug={theaterSlug}
        showTimes={formattedShowtimes}
        venueId={venue.id}
        onClick={handleShowtimeClick}
        shouldShowGroupTicketsButton={shouldShowGroupTicketsButton}
        venuePhoneNumber={venuePhoneNumber}
        analyticsPayload={analyticsPayload}
        openGroupTicketsModal={openGroupTicketsModal}
        isDisabled={showtimesDisabled}
        showSeatAvailabilityButton={isSeatAvailabilityEnabled && !hasNoShowtimes && hasAnySeatmapPreviews}
        handleToggleSeatAvailability={handleToggleSeatAvailability}
        shouldShowNewBadge={shouldShowNewBadge}
      />

      {shouldShowGroupTicketsButton && isSeatAvailabilityEnabled && (
        <GroupTicketsButtonWrapper onClick={handleGroupTicketClick} />
      )}
      {/* Seat Availability Feature */}
      {isSeatAvailabilityEnabled && !hasNoShowtimes && hasAnySeatmapPreviews && (
        <SeatAvailabilityDrawer
          isOpen={isSeatAvailabilityOpen}
          onClose={handleToggleSeatAvailability}
          venue={venue}
          showtimes={formattedShowtimes.filter((showtime) => showtime.reservable && !isShowtimePast(showtime))}
          onShowtimeClick={handleShowtimeClick}
        />
      )}
      {hasNoShowtimes && !doShowtimesHavePurchaseUrl && !isPhilippinesFreeTicketFlow && (
        <ShowtimesWithNoUrlsState
          venue={venue}
          venuePhoneNumber={venuePhoneNumber}
          analyticsPayload={analyticsPayload}
        />
      )}

      {isMarcusVenue && !hasNoShowtimes && (
        <div className="mt-4 flex gap-1">
          <BlockedIcon size={16} color="core-gray-600" />
          <CaptionSM color="core-gray-600" className="text-pretty">
            {t(
              'discountsUnavailableMaruc',
              'Discounts, Guild tickets, and Angel promotional codes cannot be used for showtimes at Marcus Theaters.',
            )}
          </CaptionSM>
        </div>
      )}
      <SingAlongModal
        showSingAlongModal={showSingAlongModal}
        setShowSingAlongModal={setShowSingAlongModal}
        handleShowtimeClick={() => selectedShowtime && handleShowtimeClick(selectedShowtime, true, true)}
      />
    </li>
  )
}
